/**
 * What « À qualifier » means, per service desk.
 *
 * The tab is not a saved filter: it is a definition. A mission belongs to it when Jira says it is
 * being qualified **and** its request type is an intervention — never an « envoi », never the DPGP
 * parent, never a mail-in request.
 *
 * **Ids, not labels.** « Demande d'intervention » exists on DPGP and means nothing on Luxe, whose
 * own types read « Parfum - Demande Intervention Technicien mobilier ». Naming the ids is what
 * makes this table reviewable against Jira, and what survives a rename.
 *
 * ⚠️ But JQL cannot filter on them. Measured 09/09 on the live stack: `?requestType=299` returns
 * **0** issue while `?requestType=Démontage` returns 119, because the clause is
 * `"Request Type" in (…)` and Jira resolves names there, not ids. So the ids below are resolved to
 * the current desk's names through `filter-options` before the listing is called — see
 * `resolveRequestTypeNames`. The status, on the other hand, does filter by id.
 *
 * A desk absent from this table has no qualification workflow identified yet: the tab is **hidden**
 * for it rather than shown empty.
 */
export interface QualificationDesk {
  /** Jira status id. `10016` = « EN COURS DE QUALIFICATION », present on both desks. */
  statusId: string;
  /** Where a send lands the ticket. `10018` = « Intervention Programmée », on both desks. */
  scheduledStatusId: string;
  /** Jira request type ids that are interventions to staff. */
  requestTypeIds: string[];
}

export const QUALIFICATION_DESKS: Record<number, QualificationDesk> = {
  // L'OREAL LUXE. The three « Demande Intervention Technicien mobilier », one per line of
  // business. Excluded: 16 / 658 / 659 (envois d'éléments manquants) and 14 (mail-in).
  3: { statusId: '10016', scheduledStatusId: '10018', requestTypeIds: ['15', '656', '657'] },

  // L'OREAL DPGP. The four children. ⚠️ 233 « Demande d'intervention » is the **parent** and is
  // deliberately out: assignment happens on the children (PO, 09/09). Also excluded: 266 and 335
  // (envois) and 232 (mail-in).
  205: { statusId: '10016', scheduledStatusId: '10018', requestTypeIds: ['299', '300', '301', '302'] },
};

export const qualificationFor = (serviceDeskId: number): QualificationDesk | null =>
  QUALIFICATION_DESKS[serviceDeskId] ?? null;

/**
 * The desk's own names for those ids. Scoped to the selected desk, so a homonym on another desk
 * cannot slip in — which is the whole reason the table above holds ids.
 *
 * Returns `null` while the options have not arrived: the caller must not impose a half-resolved
 * filter, which would list every request type instead of four.
 */
export const resolveRequestTypeNames = (
  desk: QualificationDesk,
  options: { id: string; name: string }[] | undefined
): string[] | null => {
  if (!options?.length) return null;
  const byId = new Map(options.map((option) => [option.id, option.name]));
  const names = desk.requestTypeIds.map((id) => byId.get(id)).filter((name): name is string => !!name);
  // An id the desk no longer publishes is dropped rather than fatal — Jira is the authority on
  // what exists today. All of them missing means the table is stale for this desk, and imposing
  // an empty list would silently widen the tab to everything.
  return names.length ? names : null;
};
